import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import clsx from 'clsx';
import {
  Product,
  ProductBadge,
  ProductBadgeAdditionalStyle,
  ProductBadgeMainStyle,
} from 'common/interfaces/agg-page';
import { AggPageFormContext } from 'context/page-form-context';
import { PagesContext } from 'context/pages-context';
import React, { FocusEvent, useContext, useEffect, useRef, useState } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  ArrowBackIcon,
  CrossIcon,
  LinkIcon,
  PlusOutlinedIcon,
  SearchIcon,
} from '../../../assets/images/icons';
import {
  AttributionLinkButtonIcon,
  getHardcodedLinkSourceLabel,
} from '../../../common/constants/attribution-link-source';
import { Marketplaces } from '../../../common/constants/enums';
import { lengthRestrictions } from '../../../common/constants/length-restrictions';
import { productsNumber } from '../../../common/constants/values';
import { getProductsApiClient } from '../../../common/services/products-api.service';
import { getProductsManagementApiClient } from '../../../common/services/products-management-api.service';
import { isAmazonProduct } from '../../../common/type-guards';
import { Button } from '../../../common/ui/button';
import { Container } from '../../../common/ui/container';
import { FormItem } from '../../../common/ui/formItem';
import { Image } from '../../../common/ui/image';
import { Modal } from '../../../common/ui/modal';
import { Notification } from '../../../common/ui/notification';
import { TextField } from '../../../common/ui/text-field';
import { Tooltip } from '../../../common/ui/tooltip';
import { enrichProductWithTemporaryLink, utilFormatMoney, utilProductDataPreparing } from '../../../common/utils';
import { utilCopyToClipboard } from '../../../common/utils/utilCopyToClipboard';
import {
  amazonProductAsinValidationSchema,
  walmartProductIdValidationSchema,
} from '../../../common/validationSchemas';
import { useAppSelector } from '../../../hooks';
import { generateTemporaryLinksSlice } from '../../../store/reducers/temporary-links/generate-temporary-links-slice';
import { DeleteButton } from '../../common/partials/buttons/DeleteButton';
import { ProductId, TPageForm } from '../page-form/page-form';
import { SearchProductForm } from '../page-product-input/PageProductInput';
import { CouponsModal } from './components/coupons-modal';
import { GenerateLinkModal } from './components/generate-link-modal';
import styles from './page-product.module.scss';

interface IPageProductProps {
  product: Product;
  index: number;
  marketplace?: Marketplaces;
}

export const PageProduct = ({
  index,
  product,
  marketplace = Marketplaces.Amazon,
}: IPageProductProps) => {
  const dispatch = useDispatch();
  const { publisher } = useContext(PagesContext);
  const region = publisher?.country || 'us';
  const [error, setError] = useState('');
  const [productTitle, setProductTitle] = useState<string>(
    product?.subtitle || product?.title || '',
  );
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const [isGenerateLinkModalOpen, setIsGenerateLinkModalOpen] = useState(false);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const [isBadgeEditing, setIsBadgeEditing] = useState(false);
  const [newBadgeStyle, setNewBadgeStyle] = useState<ProductBadgeMainStyle>('orange');
  const [newAdditionalStyle, setNewAdditionalStyle] =
    useState<ProductBadgeAdditionalStyle>('plain');
  const [searchProductLoading, setSearchProductLoading] = useState(false);
  const [newProduct, setNewProduct] = useState<Product>(null);
  const clickMeButtonRef = useRef<HTMLButtonElement | undefined>();
  const badgeMainInputRef = useRef<HTMLTextAreaElement>(null);
  const { coupons } = useAppSelector(({ couponsReducer }) => couponsReducer.getProductsCouponList);
  const productCoupons = coupons.filter((coupon) => coupon.asins.includes(product.product_id));
  const { control, setValue } = useFormContext<TPageForm>();
  const productContent = useWatch({ control, name: 'content' }) || [];
  const tiedContentIndex = productContent.findIndex(
    (content) => content.product_id === product.product_id && content.tied,
  );
  const rawBadge = tiedContentIndex >= 0 ? productContent[tiedContentIndex]?.badge : undefined;
  const legacyMainText = (
    rawBadge as (ProductBadge & { text?: ProductBadge['mainText'] }) | undefined
  )?.text;
  const badge = rawBadge?.mainText
    ? rawBadge
    : legacyMainText
      ? { additionalText: rawBadge?.additionalText, mainText: legacyMainText }
      : undefined;
  const badgeTextLength = [badge?.mainText.text, badge?.additionalText?.text]
    .filter(Boolean)
    .join(' ')
    .length;
  const isBadgeOverLimit = badgeTextLength > lengthRestrictions.product.badge;
  const badgeUrl = badge?.additionalText?.url || '';
  const isBadgeUrlInvalid = !!badgeUrl &&
    !badgeUrl.startsWith('/') &&
    !/^https?:\/\//i.test(badgeUrl);

  const setBadge = (nextBadge?: ProductBadge) => {
    if (tiedContentIndex < 0) return;
    setValue(`content.${tiedContentIndex}.badge`, nextBadge, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (legacyMainText && !rawBadge?.mainText) {
      setBadge({ additionalText: rawBadge?.additionalText, mainText: legacyMainText });
    }
  }, [legacyMainText, rawBadge]);

  const onBadgeTextChange = (value: string) => {
    setBadge(value
      ? {
          ...badge,
          mainText: {
            style: badge?.mainText.style || newBadgeStyle,
            text: value,
          },
        }
      : undefined);
  };

  const onAdditionalBadgeTextChange = (value: string) => {
    if (!badge) return;
    setBadge({
      ...badge,
      additionalText: value
        ? {
            style: badge.additionalText?.style || newAdditionalStyle,
            text: value,
            url: badge.additionalText?.url,
          }
        : undefined,
    });
  };

  const openBadgeEditor = () => {
    if (isBadgeEditing) {
      setIsBadgeEditing(false);
      return;
    }

    setIsBadgeEditing(true);
    setTimeout(() => badgeMainInputRef.current?.focus(), 0);
  };

  const validationSchema = marketplace === Marketplaces.Walmart
    ? walmartProductIdValidationSchema
    : amazonProductAsinValidationSchema;

  const {
    addContentCopies,
    addExcludeProductId,
    changeProductValue,
    clearChangedProductData,
    deleteProduct,
    deleteProductContentByProductId,
    deleteProductReviewsByProductId,
    productTitlesForUpdateHandler,
    products,
    replaceProduct,
    setProductsAssets,
  } = useContext(AggPageFormContext);

  const searchProductForm = useForm<SearchProductForm>({
    resolver: yupResolver(validationSchema),
  });

  const onChangeHandler = (value: string) => {
    setError('');

    const productWithBestPick = products.find((item: Product) => item.value === 'Best Pick');
    if (value === 'Best Pick' && productWithBestPick) {
      setError('Product with "Best Pick" already exists');
      return;
    }

    changeProductValue(index, value);
  };

  const replaceProductHandler = async () => {
    try {
      const {
        assets,
        reviews,
      } = utilProductDataPreparing(newProduct);

      const deletedProductId = product.product_id;

      addExcludeProductId(product.product_id, product.id);
      replaceProduct(product.product_id, newProduct);
      setProductsAssets((prevState) =>
        prevState.map((elem) => {
          if (elem.product_id !== deletedProductId) return elem;

          return {
            assets,
            product_id: newProduct.product_id,
          };
        }));
      if (isAmazonProduct(product)) {
        deleteProductReviewsByProductId(product.product_id, [reviews]);
        clearChangedProductData(product.product_id);
      }

      const contentCopiesResponse = await getProductsManagementApiClient()
        .getProductContentCopies(
          newProduct.product_id,
          region,
        );

      const contentCopies = contentCopiesResponse.data.products.reduce((result, elem) => {
        deleteProductContentByProductId(deletedProductId, [
          {
            _id: uuidv4(),
            product_id: elem.asin,
            ...({
              ...elem.content,
              faq: elem.content?.faq || [],
              pros: elem.content?.pros?.filter((p) => p?.trim().length > 0) || [],
            } || {}),
            type: 'origin',
            tied: !products.find((p) => p.id === elem.id)?.picked_product_copy_id,
          },
        ]);
        // @ts-ignore
        const copies = [];
        elem.product_copies.forEach((copy) => {
          if (copy.content) {
            copies.push({
              _id: uuidv4(),
              copy_id: copy.copy_id,
              product_id: elem.asin,
              tied: !!products.find((elem) => elem.picked_product_copy_id === copy.copy_id),
              ...{
                ...copy.content.content,
                faq: copy.content.content?.faq || [],
                pros: copy.content.content?.pros?.filter((p) => p?.trim().length > 0) || [],
              },
            });
          }
        });
        // @ts-ignore
        result = [...result, ...copies];

        return result;
      }, []);

      addContentCopies(contentCopies, true, deletedProductId);
    } catch (e) {
      console.info(e);
    }
  };

  const onProductTitleBlur = ({ target: { value } }: FocusEvent<HTMLTextAreaElement>) => {
    if (value?.trim().length) {
      productTitlesForUpdateHandler({ product_id: product.product_id, title: value });
    }
  };

  const onGenerateModalClose = () => {
    setIsGenerateLinkModalOpen(false);
    dispatch(generateTemporaryLinksSlice.actions.resetState());
  };

  const deleteProductHandler = () => {
    addExcludeProductId(product.product_id, product.id);
    deleteProduct(index);
    addContentCopies([], true, `${product.product_id}`);
    deleteProductContentByProductId(product.product_id);
  };

  const findProduct = async ({ product_id }: { product_id: ProductId }) => {
    const isExist = products
      .slice(0, productsNumber)
      .some((elem) => elem.product_id === String(product_id));

    if (isExist) {
      searchProductForm.setError('product_id', {
        message: 'Product is already added',
        type: 'value',
      });
      return;
    }

    setSearchProductLoading(true);

    try {
      let foundedProduct: Product;

      if (marketplace === Marketplaces.Amazon) {
        const productResponse = await getProductsApiClient().getProductByAsin(
          product_id as string,
          {
            features: true,
            region,
          },
        );

        foundedProduct = productResponse.data?.data?.products[0];
        if (foundedProduct) foundedProduct.product_id = productResponse.data?.data?.products[0].asin;
      }

      if (marketplace === Marketplaces.Walmart) {
        const productResponse = await getProductsApiClient().getProductById(product_id, {
          include_product_features: true,
        });

        // @ts-ignore
        foundedProduct = productResponse.data?.products[0];
        if (foundedProduct) foundedProduct.product_id = productResponse.data?.products[0].w_item_id;
      }

      if (!foundedProduct) {
        searchProductForm.setError('product_id', {
          message: 'Not found',
        });
        return;
      }

      if (product.value === 'Best Pick') {
        foundedProduct.value = 'Best Pick';
        foundedProduct.order = 1;
      }

      foundedProduct = await enrichProductWithTemporaryLink(
        foundedProduct,
        marketplace,
        publisher?.id,
        region,
      );

      setNewProduct(foundedProduct);
    } catch (e) {
      let error;
      if (axios.isAxiosError(e)) {
        error = e.message;
      } else {
        error = 'Request failed, try later';
      }
      searchProductForm.setError('product_id', {
        message: error,
        type: 'validate',
      });
    } finally {
      setSearchProductLoading(false);
    }
  };

  return (
    <Container
      className={styles.page_product_wrapper}
      onDeleteButton={deleteProductHandler}
      withDeleteButton
      withoutBorder
      withShadow
    >
      {isGenerateLinkModalOpen && <GenerateLinkModal
        asin={product.product_id || product.asin}
        initialSource={product.hardcoded_link_source}
        onClose={onGenerateModalClose}
        productId={product.product_id}
      />}
      {isReplaceModalOpen && (
        <Modal open>
          <Modal.Title>Product replacing</Modal.Title>
          <Modal.Body>
            {!newProduct && (
              <section className="mt-30 mb-30 ml-50 mr-50">
                <p className="mb-10">
                  {marketplace === Marketplaces.Walmart
                    ? 'Please, enter product id'
                    : 'Please, enter product ASIN'}
                </p>
                <div className="d-flex column-gap-10">
                  <FormItem message={searchProductForm.formState.errors?.product_id?.message}>
                    <TextField
                      disabled={searchProductLoading}
                      invalid={!!searchProductForm.formState.errors?.product_id?.message}
                      {...searchProductForm.register('product_id')}
                    />
                  </FormItem>
                  <Button
                    disabled={searchProductLoading}
                    iconStart={<SearchIcon />}
                    loading={searchProductLoading}
                    onClick={searchProductForm.handleSubmit((data) => findProduct(data))}
                  />
                </div>
              </section>
            )}
            {newProduct !== null && !searchProductLoading && (
              <section className={styles.product_preview}>
                <div className={styles.product_preview__image}>
                  <img alt="product" src={newProduct.img_medium || newProduct.img_large} />
                </div>
                <div className="flex-column raw-gap-10">
                  <h5 className={styles.product_preview__subtitle}>{newProduct.subtitle}</h5>
                  <span className={styles.product_preview__price}>
                    {!!newProduct.price && utilFormatMoney(newProduct.price)}
                  </span>
                  <span className={styles.product_preview__asin}>{newProduct.product_id}</span>
                </div>
                <DeleteButton
                  handler={() => {
                    setNewProduct(null);
                    searchProductForm.reset();
                  }}
                />
              </section>
            )}
          </Modal.Body>
          <Modal.Actions>
            <Button
              onClick={() => {
                setIsReplaceModalOpen(false);
                setNewProduct(null);
                searchProductForm.setValue('product_id', '');
              }}
              outlined
              variant="secondary"
            >
              Cancel
            </Button>
            <Button disabled={!newProduct} onClick={replaceProductHandler}>
              Replace product
            </Button>
          </Modal.Actions>
        </Modal>
      )}
      <CouponsModal
        onClose={() => setIsCouponModalOpen(false)}
        open={isCouponModalOpen}
        product_id={product.product_id}
        productCoupons={productCoupons}
      />

      <div className={styles.page_product}>
        <TextField
          className="w-100"
          disabled={product.value === 'Best Pick'}
          fullWidth
          onChange={(e) => onChangeHandler(e.target.value)}
          placeholder="Sticker"
          value={products[index].value || ''}
        />
        <div className="d-flex column-gap-10 mt-10 w-100">
          <span className={styles.asin} onClick={() => utilCopyToClipboard(product.product_id)}>
            {product.product_id}
          </span>
          <Tooltip placement="left" title={product?.is_hardcoded_link ? product.page_url : ''}>
            <Button
              disabled={!product?.is_hardcoded_link}
              iconStart={<LinkIcon />}
              onClick={() => utilCopyToClipboard(product.page_url)}
              variant="secondary"
            />
          </Tooltip>
        </div>

        {error && <Notification message={error} type="error" />}

        <Image
          alt={`product_image_${index}`}
          className={styles.image}
          src={product.img_medium || product.img_large}
        />

        <div className="w-100 flex-column raw-gap-10 mt-10 mb-10">
          <Tooltip
            className={styles.attributionButtonTooltip}
            placement="left"
            title={
              product?.is_hardcoded_link
                ? (getHardcodedLinkSourceLabel(product.hardcoded_link_source) ?? 'Attribution link')
                : ''
            }
          >
            <Button
              className={styles.card_action_button}
              fullWidth
              iconStart={(
                <AttributionLinkButtonIcon
                  className={styles.attributionSourceIcon}
                  isHardcoded={!!product?.is_hardcoded_link}
                  source={product.hardcoded_link_source}
                />
              )}
              onClick={() => setIsGenerateLinkModalOpen(true)}
              size="small"
              variant="secondary"
            >
              Attribution Link
            </Button>
          </Tooltip>
          <Button
            ref={clickMeButtonRef}
            className={clsx(styles.coupon_btn, styles.card_action_button)}
            fullWidth
            iconStart={!productCoupons.length
              ? <PlusOutlinedIcon />
              : <span aria-hidden className={styles.action_icon_placeholder} />}
            onClick={() => setIsCouponModalOpen(true)}
            size="small"
            variant="secondary"
          >
            {productCoupons.length ? 'Coupons' : 'Add coupon'}
            {!!productCoupons.length && (
              <span className={styles.count_label}>
                {productCoupons[0].discount_amount * 100}
                %
              </span>
            )}
          </Button>
        </div>

        <div className={clsx('flex-column raw-gap-10 w-100')}>
          <FormItem message={!productTitle.length && 'Field is required'}>
            <TextField
              fullWidth
              invalid={!productTitle.length}
              limit={lengthRestrictions.product.title}
              onBlur={onProductTitleBlur}
              onChange={(e) => setProductTitle(e.target.value)}
              rows={5}
              value={productTitle}
            />
          </FormItem>
          <div
            className={styles.badge_control}
            onMouseLeave={(event) => {
              const focused = document.activeElement;
              if (focused instanceof HTMLElement && event.currentTarget.contains(focused)) {
                focused.blur();
              }
              setIsBadgeEditing(false);
            }}
          >
            <div
              aria-disabled={tiedContentIndex < 0}
              aria-label={badge ? 'Edit seller badge' : 'Create seller badge'}
              className={clsx(
                styles.badge_tile,
                isBadgeOverLimit && styles.badge_tile__invalid,
                tiedContentIndex < 0 && styles.badge_tile__disabled,
              )}
              onClick={tiedContentIndex >= 0 ? openBadgeEditor : undefined}
              onKeyDown={(event) => {
                if (tiedContentIndex >= 0 && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  openBadgeEditor();
                }
              }}
              role="button"
              tabIndex={tiedContentIndex >= 0 ? 0 : -1}
            >
              <div className={styles.badge_tile__content}>
                {badge ? (
                  <div className={styles.badge_preview}>
                    <span
                      className={clsx(
                        styles.badge_preview__text,
                        styles[`badge_preview__text_${badge.mainText.style}`],
                      )}
                    >
                      {badge.mainText.text}
                    </span>
                    {badge.additionalText?.text && (
                      <span
                        className={clsx(
                          styles.badge_preview__additional,
                          badge.additionalText.style === 'link' &&
                            styles.badge_preview__additional_link,
                        )}
                      >
                        {badge.additionalText.text}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className={styles.badge_tile__placeholder}>
                    e.g. #1 Best Seller in Category
                  </span>
                )}
              </div>
              <span
                className={clsx(
                  styles.badge_tile__count,
                  isBadgeOverLimit && styles.badge_tile__count_warning,
                )}
              >
                {`${badgeTextLength}/${lengthRestrictions.product.badge}`}
              </span>
              {badge && (
                <button
                  aria-label="Clear seller badge"
                  className={styles.badge_tile__clear}
                  onClick={(event) => {
                    event.stopPropagation();
                    setBadge(undefined);
                    setIsBadgeEditing(false);
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                  type="button"
                >
                  <CrossIcon />
                </button>
              )}
            </div>
            <div
              aria-hidden={!isBadgeEditing}
              className={clsx(
                styles.badge_editor,
                isBadgeEditing && styles.badge_editor__expanded,
              )}
            >
              <div className={styles.badge_editor__inner}>
                <div className={styles.badge_editor__row}>
                  <label className={styles.badge_editor__field}>
                    <span>Main</span>
                    <TextField
                      ref={badgeMainInputRef}
                      fullWidth
                      onChange={(event) => onBadgeTextChange(event.target.value)}
                      placeholder="e.g. #1 Best Seller"
                      value={badge?.mainText.text || ''}
                    />
                  </label>
                  <select
                    aria-label="Main text style"
                    className={styles.badge_editor__style}
                    onChange={(event) => {
                      const style = event.target.value as ProductBadgeMainStyle;
                      setNewBadgeStyle(style);
                      if (badge) {
                        setBadge({
                          ...badge,
                          mainText: { ...badge.mainText, style },
                        });
                      }
                    }}
                    value={badge?.mainText.style || newBadgeStyle}
                  >
                    <option value="orange">Orange</option>
                    <option value="blue">Blue</option>
                    <option value="green">Green</option>
                    <option value="gray">Gray</option>
                    <option value="navy">Navy</option>
                  </select>
                </div>
                <div className={styles.badge_editor__row}>
                  <label className={styles.badge_editor__field}>
                    <span>Additional</span>
                    <TextField
                      fullWidth
                      onChange={(event) => onAdditionalBadgeTextChange(event.target.value)}
                      placeholder="e.g. in Category"
                      value={badge?.additionalText?.text || ''}
                    />
                  </label>
                  <select
                    aria-label="Additional text style"
                    className={styles.badge_editor__style}
                    onChange={(event) => {
                      const style = event.target.value as ProductBadgeAdditionalStyle;
                      setNewAdditionalStyle(style);
                      if (badge?.additionalText) {
                        setBadge({
                          ...badge,
                          additionalText: { ...badge.additionalText, style },
                        });
                      }
                    }}
                    value={badge?.additionalText?.style || newAdditionalStyle}
                  >
                    <option value="plain">Plain</option>
                    <option value="link">Link blue</option>
                  </select>
                </div>
                <div className={styles.badge_editor__row}>
                  <label className={styles.badge_editor__field}>
                    <span>URL (optional)</span>
                    <TextField
                      disabled={!badge?.additionalText}
                      fullWidth
                      invalid={isBadgeUrlInvalid}
                      onChange={(event) => setBadge({
                        ...badge,
                        additionalText: {
                          ...badge.additionalText,
                          url: event.target.value || undefined,
                        },
                      })}
                      placeholder="e.g. /custom-url"
                      value={badge?.additionalText?.url || ''}
                    />
                  </label>
                  <div className={styles.badge_editor__style_spacer} />
                </div>
                {isBadgeUrlInvalid && (
                  <span className={styles.badge_editor__error}>
                    Use a root-relative or HTTP(S) URL
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button
            iconStart={<ArrowBackIcon />}
            onClick={() => setIsReplaceModalOpen(true)}
            outlined
            size="small"
            variant="secondary"
          >
            Replace product
          </Button>
        </div>
      </div>
    </Container>
  );
};
